import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { resolvePatientProfile } from '@/services/patients';
import type { PrescriptionRenewalBase } from '@/services/documents';
import {
  buildMemedPatientPayload,
  cleanupMemedWidgetListeners,
  getMemedSession,
  isMemedCertificateExamFrontendEnabled,
  isMemedFrontendEnabled,
  isMemedPrescriptionOpening,
  logMemedFrontendEvent,
  MemedFrontendError,
  openMemedPrescription,
  withUxTimeout,
  isCertificateExamKind,
  memedKindOperation,
  type MemedDocumentKind,
  type MemedPrescriptionResult,
  type MemedSessionState,
} from '@/integrations/memed';

type UseMemedPrescriptionOptions = {
  patientId: string;
  onIssued?: (result: MemedPrescriptionResult) => void;
  /** Abre editor legado quando fallback técnico/disabled. */
  onLegacyFallback?: () => void;
};

/**
 * Orquestra sessão T7 + widget Memed com estados e proteção a clique duplo.
 * T10: openRenewalPrescription. T11: UX timeout + fallback observável.
 */
export function useMemedPrescription(options: UseMemedPrescriptionOptions) {
  const { patientId, onIssued, onLegacyFallback } = options;
  const [state, setState] = useState<MemedSessionState>('idle');
  const inFlightRef = useRef(false);

  useEffect(() => {
    return () => {
      cleanupMemedWidgetListeners();
    };
  }, []);

  const openWithSession = useCallback(
    async (params: {
      renewalBase?: PrescriptionRenewalBase;
      allowLegacyFallback: boolean;
      kind?: MemedDocumentKind;
    }) => {
      const kind: MemedDocumentKind = params.kind ?? 'prescription';
      const operation = params.renewalBase
        ? 'renewal_issue'
        : memedKindOperation(kind, 'open');

      if (inFlightRef.current || isMemedPrescriptionOpening()) {
        return { opened: false as const, usedLegacy: false as const };
      }

      const memedAvailable =
        isMemedFrontendEnabled() &&
        (!isCertificateExamKind(kind) || isMemedCertificateExamFrontendEnabled());

      if (!memedAvailable) {
        logMemedFrontendEvent({
          event: 'memed_fallback',
          operation,
          reason: 'feature_disabled',
        });
        if (params.allowLegacyFallback) {
          onLegacyFallback?.();
          return { opened: false as const, usedLegacy: true as const };
        }
        toast.error('Prescrição digital Memed indisponível no momento.');
        return { opened: false as const, usedLegacy: false as const };
      }

      inFlightRef.current = true;
      setState('loading');
      const startedAt = Date.now();

      let sessionToken: string | undefined;

      try {
        const run = async () => {
          const session = await getMemedSession();
          sessionToken = session.token;

          const patientRes = await resolvePatientProfile(patientId);
          if (!patientRes.success || !patientRes.data) {
            throw new MemedFrontendError({
              code: 'PATIENT_FETCH_FAILED',
              message:
                'Não foi possível carregar os dados do paciente para a prescrição digital.',
              kind: 'technical',
              // Sem paciente válido, o editor legado também não deve abrir no contexto Memed.
              allowLegacyFallback: false,
            });
          }

          const patientPayload = buildMemedPatientPayload(patientRes.data);
          const renewal = params.renewalBase;

          if (isCertificateExamKind(kind)) {
            toast.message(
              kind === 'certificate'
                ? 'No Memed, selecione Atestado. Assine com certificado em nuvem (Bird ID, VIDAS ou A3 cloud) — o .pfx do Dr House não assina.'
                : 'No Memed, selecione Pedido de exames. Assine com certificado em nuvem (Bird ID, VIDAS ou A3 cloud) — o .pfx do Dr House não assina.',
            );
          }

          await openMemedPrescription({
            token: sessionToken,
            patient: patientPayload,
            kind,
            renewalContext: renewal
              ? {
                  sourceDocumentId: renewal.sourceDocumentId,
                  sourceIssuedAt: renewal.sourceIssuedAt,
                  referenceItems: renewal.prescription?.items,
                  message: renewal.message,
                }
              : undefined,
            onIssued: (result) => {
              setState('ready');
              if (!result.providerDocumentId) {
                logMemedFrontendEvent({
                  event: 'memed_op_failure',
                  operation: memedKindOperation(kind, 'issue'),
                  durationMs: Date.now() - startedAt,
                  code: 'MEMED_ISSUE_MISSING_ID',
                });
                onIssued?.(result);
                return;
              }
              logMemedFrontendEvent({
                event: 'memed_op_success',
                operation: memedKindOperation(kind, 'issue'),
                durationMs: Date.now() - startedAt,
              });
              const successCopy =
                kind === 'certificate'
                  ? 'Atestado concluído no Memed. Registrando no App Saúde…'
                  : kind === 'exam'
                    ? 'Pedido de exame concluído no Memed. Registrando no App Saúde…'
                    : renewal
                      ? 'Nova receita digital concluída no Memed. Registrando no App Saúde…'
                      : 'Prescrição digital concluída no Memed. Registrando no App Saúde…';
              toast.success(successCopy);
              onIssued?.(result);
            },
            onCancel: () => {
              setState('idle');
            },
            onError: () => {
              // tratado no catch
            },
          });
        };

        await withUxTimeout(run());

        setState('ready');
        return { opened: true as const, usedLegacy: false as const };
      } catch (error) {
        setState('error');
        const memedError =
          error instanceof MemedFrontendError
            ? error
            : new MemedFrontendError({
                code:
                  error && typeof error === 'object' && 'code' in error
                    ? String((error as { code?: unknown }).code)
                    : 'MEMED_UNEXPECTED',
                message:
                  error instanceof Error
                    ? error.message
                    : 'Não foi possível iniciar a prescrição digital agora.',
                kind: 'technical',
                allowLegacyFallback: params.allowLegacyFallback,
              });

        const fallbackReason =
          memedError.code === 'MEMED_UX_TIMEOUT'
            ? ('timeout' as const)
            : memedError.kind === 'unavailable'
              ? ('provider_unavailable' as const)
              : memedError.kind === 'profile_incomplete'
                ? ('profile_incomplete' as const)
                : memedError.kind === 'auth'
                  ? ('auth' as const)
                  : memedError.kind === 'forbidden'
                    ? ('forbidden' as const)
                    : memedError.code?.includes('SCRIPT')
                      ? ('script_load_error' as const)
                      : ('session_error' as const);

        logMemedFrontendEvent({
          event: 'memed_op_failure',
          operation,
          reason: fallbackReason,
          code: memedError.code,
          durationMs: Date.now() - startedAt,
        });

        if (
          memedError.kind === 'profile_incomplete' ||
          memedError.kind === 'unavailable' ||
          memedError.kind === 'auth' ||
          memedError.kind === 'forbidden'
        ) {
          toast.error(memedError.message);
          return { opened: false as const, usedLegacy: false as const };
        }

        if (memedError.allowLegacyFallback && params.allowLegacyFallback) {
          logMemedFrontendEvent({
            event: 'memed_fallback',
            operation,
            reason: fallbackReason,
            code: memedError.code,
          });
          toast.error(
            `${memedError.message} Você pode continuar utilizando o editor atual.`,
          );
          onLegacyFallback?.();
          return { opened: false as const, usedLegacy: true as const };
        }

        toast.error(memedError.message);
        return { opened: false as const, usedLegacy: false as const };
      } finally {
        sessionToken = undefined;
        inFlightRef.current = false;
        setState((prev) => (prev === 'loading' ? 'idle' : prev));
      }
    },
    [onIssued, onLegacyFallback, patientId],
  );

  const openDigitalDocument = useCallback(
    async (kind: MemedDocumentKind = 'prescription') => {
      return openWithSession({ allowLegacyFallback: true, kind });
    },
    [openWithSession],
  );

  const openDigitalPrescription = useCallback(async () => {
    return openDigitalDocument('prescription');
  }, [openDigitalDocument]);

  const openRenewalPrescription = useCallback(
    async (renewalBase: PrescriptionRenewalBase) => {
      return openWithSession({
        renewalBase,
        allowLegacyFallback: false,
      });
    },
    [openWithSession],
  );

  return {
    state,
    isLoading: state === 'loading',
    openDigitalDocument,
    openDigitalPrescription,
    openRenewalPrescription,
  };
}
